import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

function CustomSelect({
  label,
  name,
  value,
  error,
  onChange,
  options,
  placeholder = 'Select option',
  disabled = false,
  searchable = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  const [query, setQuery] = useState('');
  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      String(option.label || '').toLowerCase().includes(normalizedQuery)
    );
  }, [options, query]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!selectRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function updatePlacement() {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (!triggerRect) {
        return;
      }

      const dropdownHeight = 292;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      setPlacement(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen]);

  function handleToggle() {
    if (disabled) {
      return;
    }

    setIsOpen((currentValue) => !currentValue);
  }

  function handleSelect(optionValue) {
    onChange({
      target: {
        name,
        value: optionValue,
      },
    });
    setQuery('');
    setIsOpen(false);
  }

  return (
    <div className="relative grid w-full min-w-0 self-start gap-1.5" ref={selectRef}>
      {label ? <span className="text-[0.74rem] font-medium text-[#544a42]">{label}</span> : null}
      <button
        ref={triggerRef}
        className={`flex min-h-[40px] w-full min-w-0 items-center justify-between gap-2 rounded-full border bg-[#fffdfa] px-3 py-2 text-left text-[0.84rem] outline-none transition focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)] ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[#d2bfaf] focus:border-[#7d6858]'
        } ${
          disabled
            ? 'cursor-not-allowed bg-[#f3eee8] text-[#a9998b]'
            : selectedOption
              ? 'text-[#1f1f1f]'
              : 'text-[#b3a79c]'
        }`}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label || name}
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#7c6a5a] transition ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      {error ? <span className="text-[0.74rem] text-[#bb5745]">{error}</span> : null}

      {isOpen ? (
        <div
          className={`absolute left-0 z-40 w-full min-w-0 rounded-[16px] border border-[#dccbbb] bg-white p-2 shadow-[0_18px_45px_rgba(66,45,30,0.16)] ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {searchable ? (
            <div className="relative mb-1.5">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9b8f85]"
                aria-hidden="true"
              />
              <input
                className="min-h-[34px] w-full rounded-[12px] border border-[#dccbbb] bg-[#fffdfa] pl-9 pr-3 text-[0.82rem] outline-none placeholder:text-[#b3a79c] focus:border-[#7c6a5a]"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search..."
                autoFocus
              />
            </div>
          ) : null}

          <div className="max-h-56 overflow-y-auto pr-1" role="listbox">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === String(value);

                return (
                  <button
                    key={option.value}
                    className={`flex min-h-[34px] w-full items-center justify-between gap-2 rounded-[12px] px-2.5 py-1.5 text-left text-[0.82rem] transition ${
                      isSelected
                        ? 'bg-[#f3ede7] font-semibold text-[#2d2823]'
                        : 'text-[#3d342d] hover:bg-[#faf7f3]'
                    }`}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="min-w-0 truncate">{option.label}</span>
                    {isSelected ? (
                      <Check className="h-4 w-4 shrink-0 text-[#2f8f83]" aria-hidden="true" />
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-5 text-center text-[0.8rem] text-[#8f8176]">
                No results found
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CustomSelect;
